/**
 * Default bindings for the two keyboard schemes (08 §3). Pure data.
 *
 * `modern`: WASD and the mouse. `classic`: the original's joystick-and-keyboard layout (01 §9)
 * on a PC keyboard - the arrows are the stick, Space the fire button, F1/F3/F5/F7 pick
 * carbine / grenade / LAW / knife, `5` `6` `7` arm a time bomb with a 5 / 10 / 15 s fuse,
 * Backspace (where INST/DEL sits) is first aid and the key left of `1` (the C64's left-arrow)
 * recalls the Osprey. Where the original's key is taken - the cursor keys toggled run, Space
 * toggled crawl - the modern key stands in. No default is used twice within a scheme.
 */

import type { ActionId, Scheme } from './actions';

export type BindingTable = Record<ActionId, string[]>;

const MODERN: Readonly<Record<ActionId, readonly string[]>> = {
  moveUp: ['KeyW', 'ArrowUp'],
  moveDown: ['KeyS', 'ArrowDown'],
  moveLeft: ['KeyA', 'ArrowLeft'],
  moveRight: ['KeyD', 'ArrowRight'],
  fire: ['Mouse0'],
  use: ['Mouse2'],
  run: ['ShiftLeft', 'ShiftRight'],
  crawl: ['KeyC', 'ControlLeft'],
  sneak: ['KeyX'],
  interact: ['KeyE'],
  reload: ['KeyR'],
  firstAid: ['KeyF'],
  recall: ['KeyG'],
  weapon1: ['Digit1'],
  weapon2: ['Digit2'],
  weapon3: ['Digit3'],
  weapon4: ['Digit4'],
  weapon5: ['Digit5'],
  pistol: ['Digit6'],
  smoke: ['KeyQ'],
  nextWeapon: ['WheelDown'],
  prevWeapon: ['WheelUp'],
  quickKnife: ['KeyV', 'Mouse1'],
  fuse5: ['Digit7'],
  fuse10: ['Digit8'],
  fuse15: ['Digit9'],
  map: ['Tab'],
  pause: ['Escape'],
};

const CLASSIC: Readonly<Record<ActionId, readonly string[]>> = {
  moveUp: ['ArrowUp'],
  moveDown: ['ArrowDown'],
  moveLeft: ['ArrowLeft'],
  moveRight: ['ArrowRight'],
  fire: ['Space'],
  use: ['ControlLeft', 'AltLeft'],
  run: ['ShiftLeft', 'ShiftRight'],
  crawl: ['KeyC'],
  // ROUND7: the crouched walk is not in the original; X is free in both schemes and sits beside C.
  sneak: ['KeyX'],
  interact: ['KeyE'],
  reload: ['KeyR'],
  firstAid: ['Backspace', 'Delete'],
  recall: ['Backquote'],
  weapon1: ['F1', 'Digit1'],
  weapon2: ['F3', 'Digit2'],
  weapon3: ['F5', 'Digit3'],
  // The original had no bomb key: the fuse keys below select it, as they did in 1987.
  weapon4: ['Digit4'],
  weapon5: ['F7'],
  pistol: ['Digit8'],
  smoke: ['Digit9'],
  nextWeapon: ['Period', 'WheelDown'],
  prevWeapon: ['Comma', 'WheelUp'],
  // 08 §3: in this scheme the knife is "select knife + fire"; the action can still be bound.
  quickKnife: [],
  fuse5: ['Digit5'],
  fuse10: ['Digit6'],
  fuse15: ['Digit7'],
  map: ['Tab'],
  pause: ['Escape'],
};

const BY_SCHEME: Readonly<Record<Scheme, Readonly<Record<ActionId, readonly string[]>>>> = {
  modern: MODERN,
  classic: CLASSIC,
};

/** The default keys of one action. */
export function defaultKeys(scheme: Scheme, action: ActionId): readonly string[] {
  return BY_SCHEME[scheme][action];
}

/** A fresh, mutable copy of a scheme's defaults. */
export function defaultTable(scheme: Scheme): BindingTable {
  const source = BY_SCHEME[scheme];
  const table = {} as BindingTable;
  for (const id of Object.keys(source) as ActionId[]) table[id] = [...source[id]];
  return table;
}
